import React from 'react'
import { Box, Typography } from '@mui/material'
import { CARD_SX, HAIRLINE_HOVER_COLOR } from '../../agent'
import type { ChatMessage } from './types'
import { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { GraphConfig } from '../platinum-graphs/types'
import PlatinumGraphScreenshot from '../platinum-graphs/PlatinumGraphScreenshot'
import { extractRowLabels, extractColumnLabels, extractRowTypes, extractColumnTypes, filterNonBaseFields } from '../platinum-graphs/utils/convertPlatinumData'
import { namesToIndexes, nameToIndex } from '../../utils/chartSelections'
import { validateAndNormalizeGraphCall, describeAvailableVocabulary } from '../platinum-graphs/utils/validateGraphCall'
import { isSearchOnlyRow, UI_CALL_TOOL, UiCallView } from './uiCalls'
import UiCallCaptions from './UiCallCaptions'

export interface RenderChatMessageDependencies {
  effectivePlatinumData: PlatinumData | undefined
  graphScreenshots: Map<number, string>
  setGraphScreenshots: React.Dispatch<React.SetStateAction<Map<number, string>>>
  onChatToggle: () => void
  /** What each ui_call did, by tool call id (#1534). */
  uiCalls?: Record<string, UiCallView>
  onConfirmUiCall?: (callId: string) => void
  onCancelUiCall?: (callId: string) => void
  router: {
    name: string
    params: Record<string, string>
    navigate: (name: string, params: Record<string, string>) => void
  }
}

export const renderChatMessage = (
  message: ChatMessage,
  index: number,
  deps: RenderChatMessageDependencies
): React.ReactNode | undefined => {
  // Only handle assistant messages with tool calls
  if (message.role !== 'assistant' || !message.tool_calls || message.tool_calls.length === 0) {
    return undefined
  }

  // A row that only searched the page's tools says nothing to the reader.
  if (isSearchOnlyRow(message)) return null

  const uiViews = message.tool_calls
    .filter(tc => tc.function.name === UI_CALL_TOOL)
    .map(tc => deps.uiCalls?.[tc.id])
    .filter((v): v is UiCallView => !!v)
  const graphs = renderGraphCalls(message, index, deps)
  if (uiViews.length === 0) return graphs

  const captions = <UiCallCaptions views={uiViews} onConfirm={deps.onConfirmUiCall} onCancel={deps.onCancelUiCall} />
  if (!graphs) return <Box key={index} sx={{ width: '100%' }}>{captions}</Box>
  return (
    <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
      {captions}
      {graphs}
    </Box>
  )
}

// The charts a message's render_graph calls draw (or a note when none can be).
const renderGraphCalls = (
  message: ChatMessage,
  index: number,
  deps: RenderChatMessageDependencies
): React.ReactNode | undefined => {
  const { effectivePlatinumData, graphScreenshots, setGraphScreenshots, onChatToggle, router } = deps
  if (!message.tool_calls) return undefined

  console.log('--------------------------------------------')
  console.log('Assistant message with tool_calls:', message)
  console.log('--------------------------------------------')

  // Find all render_graph tool calls
  const graphCalls = message.tool_calls.filter(tc => tc.function.name === 'render_graph')
  if (graphCalls.length === 0) {
    return undefined
  }

  // Extract row and column labels and types from data
  const allRows = effectivePlatinumData ? extractRowLabels(effectivePlatinumData) : []
  const allColumns = effectivePlatinumData ? extractColumnLabels(effectivePlatinumData) : []
  const allRowTypes = effectivePlatinumData ? extractRowTypes(effectivePlatinumData) : []
  const allColumnTypes = effectivePlatinumData ? extractColumnTypes(effectivePlatinumData) : []

  // Get non-Base fields for default selections
  const nonBaseRows = filterNonBaseFields(allRows, allRowTypes)
  const nonBaseColumns = filterNonBaseFields(allColumns, allColumnTypes)

  console.log('Available rows from PlatinumData:', allRows)
  console.log('Available columns from PlatinumData:', allColumns)
  console.log('Non-Base rows:', nonBaseRows)
  console.log('Non-Base columns:', nonBaseColumns)

  // Pre-validate all graphs to filter out ones that will error
  interface ValidatedGraph {
    graphCall: typeof graphCalls[0];
    graphConfig: GraphConfig;
    graphIndex: number;
    isValid: boolean;
    error?: string;
  }

  const validatedGraphs: ValidatedGraph[] = graphCalls.map((graphCall, graphIndex) => {
    try {
      const rawConfig: GraphConfig = JSON.parse(graphCall.function.arguments)

      const result = validateAndNormalizeGraphCall(
        rawConfig,
        allRows,
        allColumns,
        allRowTypes,
        allColumnTypes,
        effectivePlatinumData,
      )

      if (!result.isValid) {
        console.warn(`Graph ${graphIndex} validation failed:`, result.error)
      }

      return {
        graphCall,
        graphConfig: result.graphConfig,
        graphIndex,
        isValid: result.isValid,
        error: result.error,
      }
    } catch (e) {
      console.error(`Graph ${graphIndex} - Failed to parse config:`, e)
      return {
        graphCall,
        graphConfig: {} as GraphConfig,
        graphIndex,
        isValid: false,
        error: 'Failed to parse graph configuration',
      }
    }
  })

  const validGraphs = validatedGraphs.filter(g => g.isValid)

  // If no valid graphs, handle based on count
  if (validGraphs.length === 0) {
    // If there was only one graph and it failed, show a graceful message
    // instead of the raw internal error (#977). By the time this renders, the
    // hook has already given the model one real chance to retry with the
    // actual row/column vocabulary (see useTableChat.send) — this is the
    // fallback for when that retry still didn't land on valid labels.
    if (graphCalls.length === 1) {
      const graceMessage = `I couldn't build that chart — the filter didn't match any data. ${describeAvailableVocabulary(allRows, allColumns)} Try asking again using one of those labels.`
      // The message's own text is drawn by the row above this (ChatDrawer
      // passes this node as the row's extra), so only the note goes here.
      return (
        <Box key={index} sx={{ ...CARD_SX, p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {graceMessage}
          </Typography>
        </Box>
      )
    }
    // If there were multiple graphs and all failed, hide the entire message
    console.log('All graphs failed validation, hiding message')
    return null
  }

  // Only the charts: the message's text is drawn by the row above (ChatDrawer
  // passes this node as the row's extra).
  return (
    <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
          {/* Render graphs - only valid ones */}
          {validGraphs.map(({ graphConfig, graphIndex }) => {
            console.log(`Graph ${graphIndex} - Rendering validated config:`, JSON.stringify(graphConfig, null, 2))

            const screenshotKey = `${index}-${graphIndex}`
            const existingScreenshot = graphScreenshots.get(index)?.split('|||')[graphIndex]

            if (existingScreenshot) {
                // Display cached screenshot with click handler
                return (
                  <Box
                    key={graphIndex}
                    sx={{
                      ...CARD_SX,
                      p: 1.5,
                      cursor: 'pointer',
                      '&:hover': { borderColor: HAIRLINE_HOVER_COLOR },
                    }}
                    onClick={() => {
                      // Close chat drawer and open graph view
                      onChatToggle()

                      // Build params object, only including rows/cols if they differ from "all"
                      const params: Record<string, string> = {
                        view: 'graph',
                        chart_type: graphConfig.chartType,
                        data_type: graphConfig.dataType,
                        switch_axis: graphConfig.switchAxis?.toString() || 'false',
                      }

                      // Only add rows param if not all rows are selected
                      if (graphConfig.selectedRows && graphConfig.selectedRows.length !== allRows.length) {
                        params.selected_rows = namesToIndexes(graphConfig.selectedRows, allRows)
                      }

                      // Only add cols param if not all columns are selected
                      if (graphConfig.selectedColumns && graphConfig.selectedColumns.length !== allColumns.length) {
                        params.selected_columns = namesToIndexes(graphConfig.selectedColumns, allColumns)
                      }

                      // Only add col param if not the first column
                      if (graphConfig.selectedColumn && graphConfig.selectedColumn !== allColumns[0]) {
                        params.selected_column = nameToIndex(graphConfig.selectedColumn, allColumns)
                      }

                      // Clear all possible graph params first, then add back only the new ones
                      const currentParams = { ...router.params }
                      const graphParams = ['chart_type', 'data_type', 'selected_rows', 'selected_columns', 'selected_column', 'switch_axis', 'view']
                      graphParams.forEach(param => delete currentParams[param])
                      const finalParams = { ...currentParams, ...params }

                      // Navigate with clean state
                      router.navigate(router.name, finalParams)
                    }}
                  >
                      {graphConfig.explanation && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {graphConfig.explanation}
                        </Typography>
                      )}
                      {effectivePlatinumData && (
                        <PlatinumGraphScreenshot
                          data={effectivePlatinumData}
                          config={graphConfig}
                          mode="preview"
                          previewImage={existingScreenshot}
                        />
                      )}
                  </Box>
                )
              } else {
                // Capture screenshot
                return (
                  <Box key={graphIndex} sx={{ ...CARD_SX, p: 1.5 }}>
                      {graphConfig.explanation && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {graphConfig.explanation}
                        </Typography>
                      )}
                      {effectivePlatinumData && (
                        <PlatinumGraphScreenshot
                          data={effectivePlatinumData}
                          config={graphConfig}
                          mode="capture"
                          onScreenshotReady={(base64) => {
                            setGraphScreenshots(prev => {
                              const next = new Map(prev)
                              const existing = next.get(index)
                              const screenshots = existing ? existing.split('|||') : []
                              screenshots[graphIndex] = base64
                              next.set(index, screenshots.join('|||'))
                              return next
                            })
                          }}
                          displayAfterCapture={true}
                        />
                      )}
                  </Box>
                )
              }
          })}
    </Box>
  )
}
